import { useEffect, useState } from 'react'
import { useAppStore } from '../../../store/app'
import { sendRequestAgentHistory } from '../../../api/ws'
import type { HistoryItem } from '../../../store/types'
import './HistorySegment.css'

const outcomeColors: Record<HistoryItem['outcome'], string> = {
  allow: 'var(--success)',
  deny: 'var(--danger)',
  deny_stop: 'var(--danger)',
  auto_approved: 'var(--success)',
  timed_out: 'var(--label-tertiary)',
}

const outcomeLabels: Record<HistoryItem['outcome'], string> = {
  allow: 'Allowed',
  deny: 'Denied',
  deny_stop: 'Stopped',
  auto_approved: 'Auto-approved',
  timed_out: 'Timed out',
}

export function HistorySegment({ positionID }: { positionID: string }) {
  const history = useAppStore((s) => s.positionHistory[positionID] ?? [])
  const loaded = useAppStore((s) => s.historyLoadedFor[positionID] ?? false)
  const [selected, setSelected] = useState<HistoryItem | null>(null)

  useEffect(() => {
    if (!loaded) {
      sendRequestAgentHistory(positionID)
    }
  }, [positionID, loaded])

  if (!loaded) {
    return (
      <div className="history__empty">
        <div className="history__empty-title">Loading…</div>
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className="history__empty">
        <div className="history__empty-icon" aria-hidden>⏳</div>
        <div className="history__empty-title">No request history</div>
      </div>
    )
  }

  const ordered = [...history].sort((a, b) => b.occurred_at - a.occurred_at)

  return (
    <div className="history">
      <div className="history__list">
        {ordered.map((item) => (
          <button
            key={item.id}
            className="history__row"
            onClick={() => setSelected(item)}
          >
            <div className="history__row-head">
              <div className="history__row-tool">{item.tool_name}</div>
              <div
                className="history__row-outcome"
                style={{
                  color: outcomeColors[item.outcome],
                  background: `color-mix(in srgb, ${outcomeColors[item.outcome]} 15%, transparent)`,
                }}
              >
                {outcomeLabels[item.outcome]}
              </div>
            </div>
            {summary(item) && (
              <div className="history__row-summary">{summary(item)}</div>
            )}
            <div className="history__row-time">{formatTime(item.occurred_at)}</div>
          </button>
        ))}
      </div>

      {selected && (
        <HistoryDetailSheet item={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}

function HistoryDetailSheet({
  item,
  onClose,
}: {
  item: HistoryItem
  onClose: () => void
}) {
  const outcomeColor = outcomeColors[item.outcome]
  const fullCommand = fullDetail(item)

  return (
    <div className="history__detail-backdrop" onClick={onClose}>
      <div className="history__detail-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="history__detail-handle" />

        <div className="history__detail-header">
          <span className="history__detail-tool">{item.tool_name}</span>
          <span
            className="history__detail-badge"
            style={{
              color: outcomeColor,
              background: `color-mix(in srgb, ${outcomeColor} 15%, transparent)`,
            }}
          >
            {outcomeLabels[item.outcome]}
          </span>
        </div>

        {fullCommand && (
          <pre className="history__detail-command">{fullCommand}</pre>
        )}

        <div className="history__detail-rows">
          <DetailRow label="When" value={formatTimeFull(item.occurred_at)} />
          {item.responder_name ? (
            <DetailRow
              label={responderLabel(item)}
              value={item.responder_name}
            />
          ) : (
            <DetailRow
              label="Responded by"
              value={item.outcome === 'timed_out' ? 'Nobody (timed out)' : '—'}
              dim
            />
          )}
        </div>

        <button className="history__detail-close" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  )
}

function DetailRow({
  label,
  value,
  dim,
}: {
  label: string
  value: string
  dim?: boolean
}) {
  return (
    <div className="history__detail-row">
      <span className="history__detail-row-label">{label}</span>
      <span className={`history__detail-row-value${dim ? ' history__detail-row-value--dim' : ''}`}>
        {value}
      </span>
    </div>
  )
}

function responderLabel(item: HistoryItem): string {
  if (item.responder_kind === 'rule') return 'Matched rule'
  const verb = item.outcome === 'allow' ? 'Approved' : item.outcome === 'deny' || item.outcome === 'deny_stop' ? 'Denied' : 'Responded'
  const suffix = item.responder_kind === 'agent' ? ' by agent' : ' by'
  return `${verb}${suffix}`
}

function summary(item: HistoryItem): string | null {
  const cmd = item.tool_input?.command
  if (typeof cmd === 'string') return cmd.slice(0, 120)
  const path = item.tool_input?.file_path
  if (typeof path === 'string') return path
  const url = item.tool_input?.url
  if (typeof url === 'string') return url
  return null
}

function fullDetail(item: HistoryItem): string | null {
  const cmd = item.tool_input?.command
  if (typeof cmd === 'string') return cmd
  const path = item.tool_input?.file_path
  if (typeof path === 'string') return path
  const url = item.tool_input?.url
  if (typeof url === 'string') return url
  const query = item.tool_input?.query
  if (typeof query === 'string') return query
  return null
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.round(diffMs / 60_000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHrs = Math.round(diffMins / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.round(diffHrs / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatTimeFull(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
